import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Tooltip } from '@mui/material'
import { DateUtils, type Nil, PermissionArea } from '@pbt/pbt-ui-components'
import { Send as SendIcon } from '@pbt/pbt-ui-components/src/icons'

import DialogNames from '~/constants/DialogNames'
import { getCRUDByArea } from '~/store/reducers/auth'
import type { Membership, MembershipPayment, MembershipPlan } from '~/types'

import useDialog from './useDialog'

export const getMembershipPaymentId = (
  membershipPayment: Partial<MembershipPayment>,
) => `${membershipPayment.id}-${membershipPayment.subscriptionInfo?.id}`

export const isRefundableMembershipPayment = ({
  id,
  refundable,
}: Partial<MembershipPayment>) => id && refundable

export const formatMembershipPeriod = (plan: MembershipPlan | Membership) =>
  plan?.period
    ? `${DateUtils.formatDate(plan.period.startDate)}
     - ${DateUtils.formatDate(plan.period.endDate)}`
    : ''

export const useEditPaymentInfo = ({
  clientId,
  patientId,
  onClose,
}: {
  clientId: string | Nil
  onClose?: () => void
  patientId: string | Nil
}) => {
  const { t } = useTranslation('Tooltips')

  const { update: paymentUpdatePermissions } = useSelector(
    getCRUDByArea(PermissionArea.PAYMENT),
  )
  const { send } = useSelector(getCRUDByArea(PermissionArea.MEMBERSHIP_LINK))
  const membershipPaymentLinkSendPermissions = send

  const [openPatientMembershipPaymentLinkDialog] = useDialog(
    DialogNames.PATIENT_MEMBERSHIP_PAYMENT_LINK,
    onClose,
  )

  const onEditPaymentInfo = (plan: MembershipPlan) => {
    if (!(paymentUpdatePermissions || membershipPaymentLinkSendPermissions)) {
      return
    }

    openPatientMembershipPaymentLinkDialog({
      clientId,
      plan,
      patientId,
    })
  }

  const Icon = (
    <Tooltip
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -16],
            },
          },
        ],
      }}
      placement="top"
      title={t('Tooltips:SEND_UPDATE_PAYMENT_LINK')}
    >
      <SendIcon />
    </Tooltip>
  )

  return {
    onEditPaymentInfo,
    Icon,
    canUpdate: paymentUpdatePermissions || membershipPaymentLinkSendPermissions,
  }
}
